'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useRouter } from 'next/navigation'

import type { DataProviderChoice } from '@/lib/api/embeddable/embeddable.types'
import { setUserPreferences } from '@/lib/api/user-preferences/user-preferences.actions'
import { DATA_PROVIDER_KEY } from '@/lib/api/user-preferences/user-preferences.constants'
import type { UserAuthoritiesResponse } from '@/lib/api/user/user.types'

/** Where the choice lived before it became a user preference. Read once, then removed. */
const LEGACY_STAGING_LOCAL_STORAGE_KEY = 'shalion-staging-data-provider'

/** The legacy value, removing it. Storage can be unavailable (private mode, iframes). */
const takeLegacyStagingChoice = (): boolean => {
  try {
    const stored = localStorage.getItem(LEGACY_STAGING_LOCAL_STORAGE_KEY)
    localStorage.removeItem(LEGACY_STAGING_LOCAL_STORAGE_KEY)
    return stored === 'true'
  } catch {
    return false
  }
}

type UserAuthoritiesContextType = {
  isDataProviderViewEnabled: boolean
  isShalionStaff: boolean
  dataProvider: DataProviderChoice
  setDataProvider: (value: DataProviderChoice) => void
}

const UserAuthoritiesContext = createContext<UserAuthoritiesContextType | undefined>(
  undefined,
)

type UserAuthoritiesProviderProps = {
  children: React.ReactNode
  authorities?: UserAuthoritiesResponse | null
  /** The stored data provider choice, read on the server. */
  dataProvider?: DataProviderChoice
}

const UserAuthoritiesProvider: React.FC<UserAuthoritiesProviderProps> = (props) => {
  const { children, authorities, dataProvider: storedDataProvider = 'default' } = props
  const router = useRouter()

  const isDataProviderViewEnabled =
    authorities?.permissions?.dataprovider?.includes('view') ?? false

  const isShalionStaff = authorities?.permissions?.platform?.includes('staff') ?? false

  const [dataProvider, setDataProvider] = useState(storedDataProvider)

  /**
   * Save the choice, then refresh the route: the server reads it to mint the
   * Embeddable token, so the page has to render again to pick it up. Server Actions
   * run one at a time, so quick toggles are stored in order.
   */
  const handleOnChangeDataProvider = useCallback(
    (value: DataProviderChoice) => {
      setDataProvider(value)
      setUserPreferences(DATA_PROVIDER_KEY, value)
        .then(() => router.refresh())
        .catch((error: unknown) => {
          console.error('Unable to save the data provider choice', error)
        })
    },
    [router],
  )

  // One-off migration from the old browser-only storage.
  useEffect(() => {
    if (
      takeLegacyStagingChoice() &&
      isDataProviderViewEnabled &&
      storedDataProvider === 'default'
    ) {
      handleOnChangeDataProvider('staging')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, [])

  const value = useMemo(
    () => ({
      isDataProviderViewEnabled,
      isShalionStaff,
      dataProvider: isDataProviderViewEnabled ? dataProvider : 'default',
      setDataProvider: handleOnChangeDataProvider,
    }),
    [isDataProviderViewEnabled, isShalionStaff, dataProvider, handleOnChangeDataProvider],
  )

  return (
    <UserAuthoritiesContext.Provider value={value}>
      {children}
    </UserAuthoritiesContext.Provider>
  )
}

const useUserAuthorities = () => {
  const context = useContext(UserAuthoritiesContext)
  if (!context) {
    throw new Error('useUserAuthorities must be used within a UserAuthoritiesProvider')
  }
  return context
}

export { UserAuthoritiesProvider, useUserAuthorities }
